import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Wifi, WifiOff, RotateCcw } from "lucide-react";

const TEST_MS = 5000;
const LEVELS = {
  good: { label: "Great connection — ready for HD video", cls: "text-emerald-300", bar: "bg-emerald-400" },
  fair: { label: "OK connection — video may dip in quality", cls: "text-amber-300", bar: "bg-amber-400" },
  poor: { label: "Weak connection — video may freeze", cls: "text-pink-300", bar: "bg-pink-400" },
};

async function measure() {
  const pings = [];
  for (let i = 0; i < 4; i++) {
    const s = performance.now();
    try { await api.get(`/net/ping?_=${Date.now()}${i}`); } catch {}
    pings.push(performance.now() - s);
  }
  pings.sort((a, b) => a - b);
  const latency = Math.round(pings[Math.floor(pings.length / 2)]);
  let bytes = 0;
  const start = performance.now();
  while (performance.now() - start < TEST_MS - 1500) {
    try {
      const r = await api.get(`/net/probe?kb=256&_=${Date.now()}`, { responseType: "arraybuffer" });
      bytes += r.data.byteLength;
    } catch { break; }
  }
  const mbps = +((bytes * 8) / ((performance.now() - start) / 1000) / 1e6).toFixed(1);
  const level = mbps >= 1.5 && latency < 300 ? "good" : mbps >= 0.6 && latency < 600 ? "fair" : "poor";
  return { latency, mbps, level };
}

export function NetworkCheck() {
  const [res, setRes] = useState(null);
  const [progress, setProgress] = useState(0);
  const running = useRef(false);

  const run = async () => {
    if (running.current) return;
    running.current = true; setRes(null); setProgress(0);
    const t0 = Date.now();
    const iv = setInterval(() => setProgress(Math.min(100, ((Date.now() - t0) / TEST_MS) * 100)), 100);
    const out = await measure();
    clearInterval(iv); setProgress(100); setRes(out);
    running.current = false;
  };

  useEffect(() => { run(); }, []);

  const lvl = res && LEVELS[res.level];
  return (
    <div data-testid="network-check" className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-3.5">
      <div className="flex items-center gap-2 text-sm">
        {res?.level === "poor" ? <WifiOff className="h-4 w-4 text-pink-300" /> : <Wifi className={`h-4 w-4 ${lvl ? lvl.cls : "text-slate-300"}`} />}
        <span data-testid="network-check-status" className={`font-semibold ${lvl ? lvl.cls : "text-slate-200"}`}>{lvl ? lvl.label : "Checking your connection…"}</span>
        {res && <button data-testid="network-check-rerun" onClick={run} title="Re-test" className="ml-auto text-slate-400 hover:text-amber-300"><RotateCcw className="h-3.5 w-3.5" /></button>}
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mt-2.5">
        <div className={`h-full rounded-full transition-[width] duration-100 ${lvl ? lvl.bar : "bg-amber-400/70"}`} style={{ width: `${progress}%` }} />
      </div>
      {res && (
        <p data-testid="network-check-metrics" className="text-[11px] text-slate-400 mt-2">{res.mbps} Mbps down · {res.latency} ms latency</p>
      )}
      {res && res.level !== "good" && (
        <ul data-testid="network-check-tips" className="text-[11px] text-slate-300 mt-2 space-y-0.5 list-disc pl-4">
          <li>Move closer to your Wi-Fi router, or switch to 4G/5G.</li>
          <li>Pause downloads, streaming or other video calls.</li>
          <li>You can still join — turning your camera off keeps audio smooth.</li>
        </ul>
      )}
    </div>
  );
}
